import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Grid, FormControl, InputLabel, Select, MenuItem,
  Switch, FormControlLabel, Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import {
  listCin7PortConfigs,
  listCin7PortConfigScopes,
  upsertCin7PortConfig,
  deactivateCin7PortConfig
} from '../../services/inventoryService';
import api from '../../config/api';

/**
 * CIN7 Port Config Admin — multi-tenant template editor for the keys that drive
 * port-suppliers / port-warehouses / port-customer-master / port-* scripts.
 *
 * Each row is a (connector_id, config_key) pair with a JSON or scalar value.
 * JSON values are validated before save; scalar values pass through verbatim.
 */
export default function Cin7PortConfigAdmin() {
  const [rows, setRows] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [activeConnector, setActiveConnector] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editConnector, setEditConnector] = useState('');
  const [editKey, setEditKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState(null);

  // CIN7 status re-sync dialog state. Preview shows what would change before
  // the operator confirms Apply. See backend/wholesale-orders/cin7-status-sync.js
  // for the full safety contract (forward-only, only-empty-fields-overwrite).
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const openSyncDialog = async () => {
    setError(null); setSyncOpen(true);
    setSyncPreview(null);
    setSyncResult(null);
    setSyncLoading(true);
    try {
      const r = await api.get('/api/wholesale-orders/admin/cin7-status-sync/preview', {
        // No connector_id: the server uses the signed-in company's own CIN7 connector (DT-1608).
        params: { sample_size: 25 },
      });
      setSyncPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setSyncOpen(false);
    } finally {
      setSyncLoading(false);
    }
  };

  const applySync = async () => {
    if (!window.confirm(`Apply CIN7 status sync to ${syncPreview?.totals?.rows_to_update || 0} rows? This will UPDATE retail_orders.fulfillment_status / shipped_date / qb_invoice_id / qb_invoice_date / invoice_number for CIN7-source rows where CIN7 is newer.`)) return;
    setSyncLoading(true);
    try {
      const r = await api.post('/api/wholesale-orders/admin/cin7-status-sync/apply', {});
      setSyncResult(r.data);
      setSuccess(`CIN7 status sync done — ${r.data.totals?.rows_updated || 0} rows updated across ${r.data.totals?.brands_updated || 0} brand DB(s).`);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSyncLoading(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const [r, s] = await Promise.all([listCin7PortConfigs(), listCin7PortConfigScopes()]);
      setRows(r.data || []);
      setScopes(s.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (activeConnector === 'all') return rows;
    return rows.filter(r => r.connector_id === parseInt(activeConnector));
  }, [rows, activeConnector]);

  const openEdit = (row) => {
    setEditTarget(row);
    setEditConnector(row.connector_id);
    setEditKey(row.config_key);
    setEditValue(row.value_type === 'object' || row.value_type === 'array'
      ? JSON.stringify(row.parsed_value, null, 2)
      : row.config_value);
    setEditNotes(row.notes || '');
    setEditActive(row.is_active === 1);
    setJsonError(null);
    setError(null); setEditOpen(true);
  };

  const openCreate = () => {
    setEditTarget(null);
    // Default to first known connector_id if any exist
    setEditConnector(scopes[0]?.connector_id || '');
    setEditKey('');
    setEditValue('');
    setEditNotes('');
    setEditActive(true);
    setJsonError(null);
    setError(null); setEditOpen(true);
  };

  // Validate JSON as the user types — show issue but allow save if scalar
  useEffect(() => {
    if (!editOpen) return;
    const v = editValue.trim();
    if (!v) { setJsonError(null); return; }
    if (/^[\[{"]/.test(v) || /^\d/.test(v) || v === 'true' || v === 'false' || v === 'null') {
      try { JSON.parse(v); setJsonError(null); }
      catch (e) { setJsonError(e.message); }
    } else {
      setJsonError(null);
    }
  }, [editValue, editOpen]);

  const submitEdit = async () => {
    if (!editConnector || !editKey.trim()) {
      setError('connector_id and config_key are required');
      return;
    }
    if (jsonError) {
      setError(`JSON validation failed: ${jsonError}`);
      return;
    }
    try {
      setSaving(true);
      await upsertCin7PortConfig({
        connector_id: parseInt(editConnector),
        config_key: editKey.trim(),
        config_value: editValue,
        notes: editNotes,
        is_active: editActive ? 1 : 0
      });
      setSuccess(`Saved ${editKey.trim()}`);
      setEditOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  const handleDeactivate = async (row) => {
    if (!window.confirm(`Deactivate "${row.config_key}"? Port scripts will skip it.`)) return;
    try {
      await deactivateCin7PortConfig(row.id);
      setSuccess(`Deactivated ${row.config_key}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">CIN7 Port Config</Typography>
          <Typography variant="body2" color="textSecondary">
            Per-tenant key/value config that drives the CIN7 port scripts (fuzzy maps, country→subsidiary,
            send-method-by-retailer, sheet IDs). RMT — re-saving the same key updates in place.
          </Typography>
        </Box>
        <Box>
          <Button variant="outlined" color="warning" onClick={openSyncDialog} sx={{ mr: 1 }}>
            Sync CIN7 Order Statuses
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={load} sx={{ mr: 1 }}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {scopes.length > 1 && (
        <FormControl size="small" sx={{ mb: 2, minWidth: 240 }}>
          <InputLabel>Connector</InputLabel>
          <Select value={activeConnector} label="Connector" onChange={(e) => setActiveConnector(e.target.value)}>
            <MenuItem value="all">All connectors ({rows.length})</MenuItem>
            {scopes.map(s => (
              <MenuItem key={s.connector_id} value={String(s.connector_id)}>
                Connector #{s.connector_id} ({s.row_count} keys)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Connector</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Preview</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(r => (
                  <TableRow key={r.id} hover sx={{ opacity: r.is_active ? 1 : 0.5 }}>
                    <TableCell>{r.connector_id}</TableCell>
                    <TableCell><strong>{r.config_key}</strong></TableCell>
                    <TableCell>
                      <Chip size="small" label={r.value_type}
                        color={r.value_type === 'object' || r.value_type === 'array' ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 380, fontFamily: 'monospace', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={<pre style={{ margin: 0, maxHeight: 400, overflow: 'auto' }}>{r.config_value}</pre>}>
                        <span>{r.config_value.length > 90 ? `${r.config_value.slice(0, 90)}…` : r.config_value}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240, fontSize: 12 }}>{r.notes}</TableCell>
                    <TableCell>{r.is_active ? <Chip size="small" label="active" color="success" /> : <Chip size="small" label="inactive" />}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(r)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                      {r.is_active === 1 && (
                        <IconButton size="small" onClick={() => handleDeactivate(r)} title="Deactivate"><DeleteIcon fontSize="small" /></IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center">
                    <Typography color="textSecondary" sx={{ py: 2 }}>No config rows.</Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editTarget ? `Edit "${editTarget.config_key}"` : 'New Config'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="Connector ID" type="number" value={editConnector}
                onChange={(e) => setEditConnector(e.target.value)}
                disabled={!!editTarget} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Key" value={editKey}
                onChange={(e) => setEditKey(e.target.value)}
                disabled={!!editTarget}
                placeholder="e.g. supplier_fuzzy_map" />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={editValue.includes('\n') || editValue.length > 60 ? 14 : 4}
                label="Value (JSON for objects/arrays, raw text for IDs/strings)"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                error={!!jsonError}
                helperText={jsonError ? `JSON parse error: ${jsonError}` : ' '}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />}
                label={editActive ? 'Active (port scripts will read this row)' : 'Inactive (port scripts will skip)'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submitEdit} variant="contained" startIcon={<SaveIcon />} disabled={saving || !!jsonError}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CIN7 status sync dialog — preview then apply. Forward-only progression
          and only-when-empty for date/invoice fields; see backend safety rails. */}
      <Dialog open={syncOpen} onClose={() => !syncLoading && setSyncOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Sync CIN7 Order Statuses</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Re-imports <strong>fulfillment_status</strong>, <strong>shipped_date</strong>, <strong>invoice_number</strong>,
            <strong> qb_invoice_id</strong>, and <strong>qb_invoice_date</strong> from CIN7 for existing source='cin7' rows
            where CIN7's state is newer. Forward-only — never downgrades shipped → pending. Date and invoice fields
            are only set when ours are currently empty. Voided rows (is_voided=1) are skipped.
          </Typography>

          {syncLoading && !syncPreview && (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          )}

          {syncPreview && !syncResult && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Rows examined: ${syncPreview.totals.rows_examined}`} />
                <Chip color={syncPreview.totals.rows_to_update > 0 ? 'warning' : 'success'}
                  label={`Rows to update: ${syncPreview.totals.rows_to_update}`} />
              </Box>

              {syncPreview.brands.filter(b => b.rows_to_update > 0).length === 0 ? (
                <Alert severity="success">Everything's already in sync. No changes needed.</Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Brand</TableCell>
                        <TableCell>Order</TableCell>
                        <TableCell>Field</TableCell>
                        <TableCell>From</TableCell>
                        <TableCell>To</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {syncPreview.brands.flatMap(b => b.sample.flatMap(p =>
                        Object.entries(p.changes).map(([field, vals]) => (
                          <TableRow key={`${b.brand_id}-${p.external_order_id}-${field}`}>
                            <TableCell><Chip size="small" label={b.brand_name} /></TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                              {p.order_name || p.order_id}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{field}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.disabled' }}>
                              {vals.from === null || vals.from === '' ? '—' : String(vals.from)}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600 }}>
                              {String(vals.to)}
                            </TableCell>
                          </TableRow>
                        ))
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {syncPreview.totals.rows_to_update > syncPreview.brands.reduce((s, b) => s + b.sample.length, 0) && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Showing first {syncPreview.brands.reduce((s, b) => s + b.sample.length, 0)} of {syncPreview.totals.rows_to_update}.
                  Apply will process all of them.
                </Typography>
              )}
            </>
          )}

          {syncResult && (
            <Alert severity="success" sx={{ mb: 1 }}>
              <Typography variant="body2"><strong>Sync complete.</strong></Typography>
              <Typography variant="body2">
                {syncResult.totals.rows_updated} row(s) updated across {syncResult.totals.brands_updated} brand DB(s).
              </Typography>
              {syncResult.brands.filter(b => b.error).map(b => (
                <Typography key={b.brand_id} variant="caption" color="error" sx={{ display: 'block' }}>
                  {b.brand_name}: {b.error}
                </Typography>
              ))}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncOpen(false)} disabled={syncLoading}>
            {syncResult ? 'Close' : 'Cancel'}
          </Button>
          {syncPreview && !syncResult && syncPreview.totals.rows_to_update > 0 && (
            <Button onClick={applySync} variant="contained" color="warning" disabled={syncLoading}>
              {syncLoading ? 'Applying…' : `Apply (${syncPreview.totals.rows_to_update})`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
